import React, { FC, useState, useEffect, useCallback, KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import './PokerHand.css'; // Import the new CSS file
// Import our data types and mock data
import { NewHandSummaryJson, Street as AppStreet, HandSummaryActionWithPlayerId, HandSummaryPlayer } from './BuddyPage';
import { mockHandData as appMockHandData } from './PokerHand.data'; // Assuming mock data is moved


// ---------- Types (Keep Sample Types for Components) ----------
type Suit = 's' | 'h' | 'd' | 'c';
type CardCode = `${string}${Suit}`; // e.g. "Ah", "Td"
// Allow 'back' as a card representation
type CardRepresentation = CardCode | 'back';

// Player type expected by PlayerRow component
interface ComponentPlayer {
  id: number; // Use number to match source data
  name: string;
  cards: CardRepresentation[];
  chips: number;
  winner?: boolean;
  revealAlways?: boolean; // Used in stepper logic
  isFolded?: boolean; // Used for styling PlayerRow
  isHero?: boolean; // Used for revealAlways logic
  position: string; // Add position field
}

// Define structure for a bet placed on the table
// interface PlacedBet { ... }

interface StepperProps {
  title?: string;
  pot?: number;
  board: CardCode[];
  players: ComponentPlayer[];
  currentStreetIndex: number; 
  currentActionIndex: number; 
  onStepChange: (direction: 'next' | 'prev') => void; 
  handDataSource: NewHandSummaryJson | null; 
  animatedChips: AnimatedChipData[]; // Pass array of chips to animate/display
  seatMap: Map<string, SeatMapEntry>; // Keep passing seatMap for positioning
}

// We are focusing on the 'replay' mode (Stepper) for now
// interface TrainerProps { ... }
// interface WrapperProps { ... }

// ---------- Constants ----------

// Restore non-layout constants to top-level
const suitGlyph: Record<Suit, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };
const INDEX_TO_APP_STREET: AppStreet[] = ['preflop', 'flop', 'turn', 'river'];
const STREETS = ['Pre‑flop', 'Flop', 'Turn', 'River', 'Showdown'] as const;

// Group layout constants into a config object
const tableConfig = {
    SVG_VIEWBOX_WIDTH: 1500,
    SVG_VIEWBOX_HEIGHT: 980,
    TABLE_CX: 1500 / 2,
    TABLE_CY: 980 / 2,
    TABLE_RX: 630,
    TABLE_RY: 375,
    BORDER_WIDTH: 30,
    SEAT_RADIUS: 35,
    CHIP_SVG_DIAMETER: 40,
    CARD_SVG_WIDTH: 70,
    CARD_SVG_HEIGHT: 105,
    ELEMENT_PADDING: 15, 
    BET_LINE_PADDING: 120, 
    BET_CHIP_TARGET_OFFSET: 50, // Increased additional inset further (was 35)
    CHIP_ANGULAR_OFFSET_DEG: 15, 
    HERO_TARGET_ANGLE: -50, 
    POSITION_ORDER_9_HANDED: ['SB', 'BB', 'UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN'],
    CARD_CORNER_RADIUS: 7,
    BOARD_CARD_SPACING: 6,
    CARD_SPACING: 6, 
};

// Define desired render size separately from internal drawing size
const CHIP_RENDER_DIAMETER = tableConfig.CHIP_SVG_DIAMETER; // Use config value
// Define internal drawing size (keep consistent with SvgChip component)
const CHIP_INTERNAL_RADIUS = 50;

// Use config values in functions/components where needed

// ---------- SVG / Geometry Helpers ----------

// ADD polarEllipse function
function polarEllipse(
  cx: number, cy: number,
  rx: number, ry: number,
  fraction: number,  
  extraRotationRad = 0 
): { x: number; y: number } {
  // baseAngle starts at –90° so fraction=0 is "12 o'clock"
  const baseAngle = 2 * Math.PI * fraction - Math.PI/2; 
  const θ = baseAngle + extraRotationRad; // Apply rotation offset
  return {
    x: cx + rx * Math.cos(θ),
    y: cy + ry * Math.sin(θ),
  };
}

// ---------- Chip Calculation & SVG Components ---------- 

// Re-introduce denominations and calculation function
const denominations = [500, 100, 25, 5, 1]; // Example denominations

function calculateChipCounts(total: number): Record<number, number> {
  const counts: Record<number, number> = {};
  let remaining = total;

  for (const denom of denominations) {
    if (remaining <= 0) break;
    const numChips = Math.floor(remaining / denom);
    if (numChips > 0) {
      counts[denom] = numChips;
      remaining -= numChips * denom;
    }
  }
  if (remaining > 0 && denominations.includes(1)) {
     counts[1] = (counts[1] || 0) + Math.ceil(remaining); 
  }
  return counts;
}

// Define Chip Appearance Mapping
interface ChipStyle {
    mainFill: string;
    centerFill: string;
    textFill: string;
    valueText: string;
}

const chipStyles: Record<number, ChipStyle> = {
    1:   { mainFill: '#ff8c00', centerFill: '#cc7000', textFill: '#ffffff', valueText: '1' }, // Dark Orange
    5:   { mainFill: '#c62828', centerFill: '#a12020', textFill: '#ffffff', valueText: '5' },
    25:  { mainFill: '#2E8B57', centerFill: '#1e603b', textFill: '#ffffff', valueText: '25' },
    100: { mainFill: '#37474F', centerFill: '#111111', textFill: '#ffffff', valueText: '100' }, // Dark grey/black
    500: { mainFill: '#6a0dad', centerFill: '#4a097d', textFill: '#ffffff', valueText: '500' }, // Purple
    // Add more denominations/colors as needed
};

const defaultChipStyle: ChipStyle = { mainFill: '#777777', centerFill: '#555555', textFill: '#ffffff', valueText: '?' };

// Add SvgChipProps interface back
interface SvgChipProps {
    denomination: number;
    className?: string; 
}

// SvgChip Component - Internal coordinates remain based on radius 50
const SvgChip: FC<SvgChipProps> = ({ denomination, className = '' }) => {
    const style = chipStyles[denomination] || defaultChipStyle;
    const valueStr = style.valueText;
    const fontSize = valueStr.length <= 1 ? 30 : valueStr.length === 2 ? 28 : 24; 
    const yPos = 55;

    // Return a group <g> instead of a full <svg>
    return (
        <g className={`svg-chip ${className}`}> 
           {/* Draws centered around (55, 55) with r=50 internally */}
           {/* Keep internal coords for now, recentering is complex */}
            <circle cx="55" cy="55" r={CHIP_INTERNAL_RADIUS} fill={style.mainFill} filter="url(#chip-shadow)" />
            {/* Stripes (rest of chip) */}
            <g clipPath="url(#rimClipSvg)">
                 {/* Use elements for stripes */}
                 <use href="#edgeStripeSvg"/>
                 <use href="#edgeStripeSvg" transform="rotate(45 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(90 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(135 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(180 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(225 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(270 55 55)"/>
                 <use href="#edgeStripeSvg" transform="rotate(315 55 55)"/>
            </g>
            <circle cx="55" cy="55" r="42.5" fill="none" stroke="#ffffff" strokeWidth="3"/>
            <circle cx="55" cy="55" r="34" fill={style.centerFill} />
            <text 
                x="55" 
                y={yPos} 
                fontSize={fontSize} 
                fill={style.textFill} 
                dominantBaseline="central"
                textAnchor="middle"
            >
                {valueStr}
            </text>
        </g>
    );
};

// ChipStacksSvg Component - Remove x/y props
interface ChipStacksSvgProps {
  chips: number;
  isFolded: boolean;
  fraction: number;
  rotationOffsetRad: number;
  chipRx: number;
  chipRy: number;
}

// const CHIP_SVG_VIEWBOX_SIZE = 110; // Remove - use 2*CHIP_INTERNAL_RADIUS
const CHIP_SCALE = CHIP_RENDER_DIAMETER / (2 * CHIP_INTERNAL_RADIUS);
// const CHIP_COUNT_Y_OFFSET = ... // Keep this relative? Or adjust based on internal radius?
// Let's keep count text position relative to internal viewBox for now
const CHIP_SVG_CENTER = 55; // Center of internal drawing

const ChipStacksSvg: FC<ChipStacksSvgProps> = ({ chips, isFolded, fraction, rotationOffsetRad, chipRx, chipRy }) => {
  const chipCounts = calculateChipCounts(chips);
  const sortedDenominations = Object.keys(chipCounts).map(Number).sort((a, b) => b - a);
  const numDenominations = sortedDenominations.length;
  
  // Calculate spacing based on fraction of circumference
  const chipVisualSpacing = CHIP_RENDER_DIAMETER * 0.8; // Reduced multiplier from 1.1
  // Approximate circumference using average radius
  const avgRadius = (chipRx + chipRy) / 2;
  const approxCircumference = 2 * Math.PI * avgRadius;
  const fracSpacing = approxCircumference > 0 ? chipVisualSpacing / approxCircumference : 0;

  // Main group can still use x,y if needed for transform, or ignore them
  // const groupTransform = `translate(${x}, ${y})`; // Optional: Keep translate if group positioning useful

  return (
    <g 
       // transform={groupTransform} 
       className={`chip-stacks-svg-group ${isFolded ? 'chip-stacks-svg-group--folded' : ''}`}
       opacity={isFolded ? 0.6 : 1} 
    >
       {/* Chip rendering loop calculates absolute positions */} 
       {sortedDenominations.map((denom, index) => {
         const count = chipCounts[denom];
         
         // Calculate fractional offset for this specific chip
         const fracOffset = (index - (numDenominations - 1) / 2) * fracSpacing;
         const currentChipFrac = fraction + fracOffset;

         // Calculate the exact position for THIS chip's center on the ellipse
         const { x: chipX, y: chipY } = polarEllipse(
             tableConfig.TABLE_CX, 
             tableConfig.TABLE_CY, 
             chipRx, 
             chipRy, 
             currentChipFrac, // Use fraction adjusted for spacing
             rotationOffsetRad
         );

         // Adjust transform origin to place chip center correctly
         const chipTranslateX = chipX - CHIP_SVG_CENTER * CHIP_SCALE;
         const chipTranslateY = chipY - CHIP_SVG_CENTER * CHIP_SCALE;
         const chipTransform = `translate(${chipTranslateX}, ${chipTranslateY}) scale(${CHIP_SCALE})`; 
         
         // Keep count text position relative to internal chip center
         const countX = CHIP_SVG_CENTER; 
         const countY = CHIP_SVG_CENTER + CHIP_INTERNAL_RADIUS + 20;

         return (
            <g key={denom} className="chip-svg-denomination-group" transform={chipTransform}>
               <SvgChip denomination={denom} />
               <text x={countX} y={countY} fontSize="28" fill="#d0d0d0" fontWeight="bold" textAnchor="middle" dominantBaseline="middle" className="chip-svg-count-svg" >x{count}</text>
            </g>
         );
       })}
    </g>
  );
};


// ---------- Atoms (Updated implementation from sample) ----------
// Card component (Refactored for SVG)
interface CardProps {
    code: CardCode;
    show: boolean;
    x: number; // Relative x position within PlayerCards/Board
    y: number; // Relative y position within PlayerCards/Board
}

// Reduce card size slightly
const CARD_CORNER_RADIUS = 8; // Reduced slightly

const Card: FC<CardProps> = ({ code, show, x, y }) => {
    // Base transform for the card group - CENTER the card at x, y
    const groupTransform = `translate(${x - tableConfig.CARD_SVG_WIDTH/2}, ${y - tableConfig.CARD_SVG_HEIGHT/2})`;

    // Define SvgBack component locally (could be moved)
    const SvgBack = () => (
        <g>
          {/* Use the globally defined lattice pattern */}
          <rect width={tableConfig.CARD_SVG_WIDTH} height={tableConfig.CARD_SVG_HEIGHT} rx={tableConfig.CARD_CORNER_RADIUS} ry={tableConfig.CARD_CORNER_RADIUS} fill="#23305a"/> 
          <rect width={tableConfig.CARD_SVG_WIDTH} height={tableConfig.CARD_SVG_HEIGHT} rx={tableConfig.CARD_CORNER_RADIUS} ry={tableConfig.CARD_CORNER_RADIUS} fill="url(#lattice)"/>
        </g>
    );
    
    // Define SvgFront component locally
    const SvgFront: FC<{rank: string; suit: Suit}> = ({rank, suit}) => {
        const color = (suit === 'h' || suit === 'd') ? 'red' : 'black';
        const glyph = suitGlyph[suit];
        return (
          <g>
            <rect width={tableConfig.CARD_SVG_WIDTH} height={tableConfig.CARD_SVG_HEIGHT} rx={tableConfig.CARD_CORNER_RADIUS} ry={tableConfig.CARD_CORNER_RADIUS} fill="white"/>
            {/* Position rank/suit - adjust fontSize/coords as needed */}
            <text 
              x="8" 
              y="20" // Adjust text position for new size
              fontSize="20" // Adjust font size
              fontWeight="bold"
              fill={color}
              textAnchor="start"
            >
                {rank}
            </text>
            <text 
               x="8" 
               y="40" // Adjust text position
               fontSize="18" // Adjust font size
               fill={color}
               textAnchor="start"
            >
                {glyph}
            </text>
            {/* Add large center suit? Optional */}
            <text 
                x={tableConfig.CARD_SVG_WIDTH / 2}
                y={tableConfig.CARD_SVG_HEIGHT / 2 + 5} // Adjust position
                fontSize="35" // Adjust size
                fill={color}
                textAnchor="middle"
                dominantBaseline="middle"
            >
                {glyph}
             </text>
            {/* Bottom right corner (rotated) */}
            <g transform={`translate(${tableConfig.CARD_SVG_WIDTH - 8}, ${tableConfig.CARD_SVG_HEIGHT - 12}) rotate(180)`}> // Adjust position
                <text 
                   x="0" 
                   y="12" // Adjust position
                   fontSize="20" // Adjust size
                   fontWeight="bold"
                   fill={color}
                >
                    {rank}
                </text>
                 <text 
                   x="0" 
                   y="32" // Adjust position
                   fontSize="18" // Adjust size
                   fill={color}
                   textAnchor="start"
                 >
                    {glyph}
                </text>
             </g>
           </g>
        );
    }

    // Determine rank/suit if showing front
    let rank = '', suit: Suit | null = null;
    if (show && code && code.length >= 2) {
      rank = code[0];
      suit = code[1] as Suit;
    }

    return (
        <g transform={groupTransform}>
            {show && suit 
                ? <SvgFront rank={rank} suit={suit} /> 
                : <SvgBack />
            }
        </g>
    );
}

// Board component (Refactored for SVG)
interface BoardProps {
    cards: CardCode[]; 
    street: number; // 0-4
    // Add props for positioning within the main SVG
    x: number;
    y: number;
}

const BOARD_CARD_SPACING = 8; // Reduced slightly

const Board: FC<BoardProps> = ({ cards, street, x, y }) => {
  const revealCount = street === 0 ? 0 : street === 1 ? 3 : street === 2 ? 4 : 5;
  const actualCards = cards || [];
  const groupTransform = `translate(${x}, ${y})`;
  const totalWidth = 5 * tableConfig.CARD_SVG_WIDTH + 4 * tableConfig.BOARD_CARD_SPACING;
  const startOffset = -totalWidth / 2; // Adjust start offset for centered cards

  // Prepare list of cards to actually render and animate
  const revealedBoardCards = actualCards.slice(0, revealCount);

  return (
    <g transform={groupTransform}>
      {/* Render Placeholders first */} 
      {[...Array(5)].map((_, index) => {
        const cardCenterX = startOffset + (index * (tableConfig.CARD_SVG_WIDTH + tableConfig.BOARD_CARD_SPACING)) + tableConfig.CARD_SVG_WIDTH / 2;
        const cardCenterY = 0;
        return (
            <rect 
               key={`placeholder-${index}`}
               x={cardCenterX - tableConfig.CARD_SVG_WIDTH / 2} 
               y={cardCenterY - tableConfig.CARD_SVG_HEIGHT / 2}
               width={tableConfig.CARD_SVG_WIDTH} 
               height={tableConfig.CARD_SVG_HEIGHT} 
               rx={tableConfig.CARD_CORNER_RADIUS} 
               fill="rgba(0,0,0,0.2)" 
               stroke="rgba(255,255,255,0.2)" 
               strokeDasharray="5 5" 
            />
        );
      })}
      {/* Animate revealed cards */} 
      <AnimatePresence>
         {revealedBoardCards.map((cardCode, index) => {
             const cardCenterX = startOffset + (index * (tableConfig.CARD_SVG_WIDTH + tableConfig.BOARD_CARD_SPACING)) + tableConfig.CARD_SVG_WIDTH / 2;
             const cardCenterY = 0;
             return (
                <motion.g
                    key={cardCode + index} // Use card code and index for unique key
                    initial={{ x: cardCenterX + 50, opacity: 0 }} // Start offset to the right
                    animate={{ x: cardCenterX, opacity: 1 }} // Animate to final position
                    exit={{ x: cardCenterX - 50, opacity: 0 }} // Animate out to the left (optional)
                    transition={{ duration: 0.3, delay: index * 0.1 }} // Stagger animation
                    // Apply motion to the group's x coordinate only
                    style={{ x: cardCenterX, y: cardCenterY }} // Use style for x,y? Or just pass to Card?
                    // Need to adjust how coords are passed if motion controls position
                >
                    <Card 
                        code={cardCode}
                        show={true} 
                        // Card component now expects center coords relative to its parent group 
                        // Since motion.g handles the position, Card is at 0,0 relative to it
                        x={0} 
                        y={0} 
                     />
                </motion.g>
             );
         })}
      </AnimatePresence>
    </g>
  );
};

// --- NEW PlayerCards component ---
interface PlayerCardsProps {
  cards: CardRepresentation[];
  show: boolean;
  isFolded: boolean;
  x: number;
  y: number;
  cardRotationDeg: number; // Rotation angle for the whole group
}

// Spacing between the two hole cards
const CARD_SPACING = tableConfig.CARD_SPACING; 

const PlayerCards: FC<PlayerCardsProps> = ({ cards, show, isFolded, x, y, cardRotationDeg }) => {
  // Apply rotation to the main group transform
  const groupTransform = `translate(${x}, ${y}) rotate(${cardRotationDeg})`; 
  const numCards = cards?.length || 0;

  return (
    <g
      transform={groupTransform} // Apply translation AND rotation
      className={`player-cards-svg-group ${isFolded ? 'player-cards-svg-group--folded' : ''}`}
      opacity={isFolded ? 0 : 1}
    >
      {(cards || []).map((cardCode, index) => {
        // Calculate position relative to the group center (0,0) which is already rotated
        const cardCenterX = (index - (numCards - 1) / 2) * (tableConfig.CARD_SVG_WIDTH + CARD_SPACING);
        const cardCenterY = 0; 
        
        const currentCode = cardCode === 'back' ? 'Xx' as CardCode : cardCode;
        const currentShow = cardCode === 'back' ? false : show;
        // Remove fanning angle calculation
        // const fanAngle = (index - (numCards - 1) / 2) * 8; 
        
        return (
          // Apply only local translation *within* the main rotated group
          <g key={`${cardCode}-${index}`} transform={`translate(${cardCenterX}, ${cardCenterY})`}>
             <Card 
               code={currentCode}
               show={currentShow} 
               x={0} 
               y={0} 
             />
          </g>
        );
      })}
    </g>
  );
};

// --- PlayerSeat component (Refactored for SVG) ---
interface PlayerSeatProps {
  position: string; 
  isHero?: boolean;
  isFolded?: boolean;
  x: number;
  y: number;
  // No angle prop
}

const PlayerSeat: FC<PlayerSeatProps> = ({ position, isHero, isFolded, x, y }) => {
  const groupTransform = `translate(${x}, ${y})`; // Only translate
  
  return (
    <g 
      transform={groupTransform} 
      className={`ph-player-seat-svg ${isFolded ? 'ph-player-seat-svg--folded' : ''} ${isHero ? 'ph-player-seat-svg--hero' : ''}`}
    >
      {/* Seat Circle (centered at 0,0 relative to the group) */}
      <circle 
        cx="0" 
        cy="0" 
        r={tableConfig.SEAT_RADIUS} 
        // Use fill/stroke attributes instead of CSS background/border
        fill="url(#seatGradient)" // Define gradient below
        stroke={isHero ? '#ffd700' : '#1a3f7a'} 
        strokeWidth="3" 
        // Add SVG filter for shadow?
        // filter="url(#seatShadow)"
      />
      {/* Position Text */}
      <text 
        x="0" 
        y="0" 
        fill="#f0e0c0" 
        textAnchor="middle" 
        fontSize="18" // Adjust size as needed
        fontWeight="bold" 
        dominantBaseline="central"
      >
        {position}
      </text>
      {/* Define Gradient in SVG Defs (needs to be added to PokerHandStepper's <defs>) */}
      {/* Example Definition (Add this inside <defs> in PokerHandStepper):
      <linearGradient id="seatGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style={{stopColor:'#2b6cb0', stopOpacity:1}} />
        <stop offset="100%" style={{stopColor:'#1a3f7a', stopOpacity:1}} />
      </linearGradient>
      <filter id="seatShadow" x="-30%" y="-30%" width="160%" height="160%">
         <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.5"/>
      </filter>
      */}
    </g>
  );
};


// ---------- Stepper Data Adaptation ----------

// Function to get all actions up to the current point in the hand
const getActionsUpToCurrent = (
    handData: NewHandSummaryJson,
    currentStreetIndex: number, // Use index 0-3
    currentActionIndex: number // Index within the specific street's actions
): HandSummaryActionWithPlayerId[] => {
    let actions: HandSummaryActionWithPlayerId[] = [];
    for (let i = 0; i <= currentStreetIndex; i++) {
        const street = INDEX_TO_APP_STREET[i];
        if (!street) continue; // Boundary check
        // Check if the street exists in handData before accessing actions
        if (handData[street]) {
            if (i < currentStreetIndex) {
                actions = actions.concat(handData[street]?.actions || []);
            } else {
                // Only include actions up to current index on the current street
                actions = actions.concat((handData[street]?.actions || []).slice(0, currentActionIndex + 1));
            }
        }
    }
    return actions;
};

// Prepare data for PlayerRow components based on current state
const getPlayerDataForRender = (
    handData: NewHandSummaryJson,
    currentStreetIndex: number, // Use index 0-4 (0-3 for streets, 4 for showdown)
    currentActionIndex: number
): ComponentPlayer[] => {
    if (!handData || !handData.players) return []; // Add null check for players

    // Determine actions relevant up to this exact step
    const relevantStreetIndex = Math.min(currentStreetIndex, 3); // Actions only happen up to river (index 3)
    const actions = getActionsUpToCurrent(handData, relevantStreetIndex, currentActionIndex);

    return handData.players.map(p => {
        let currentChips = p.initialStack;
        let hasFolded = false;

        // Calculate chips and folded status
        actions.forEach(action => {
            if (action.playerId === p.id) {
                if (action.type === 'Bet' || action.type === 'Raise' || action.type === 'Call') {
                    currentChips -= action.amount || 0;
                } else if (action.type === 'Fold') {
                    hasFolded = true;
                }
                // Add SB/BB post handling if not implicitly included in 'Call'/'Raise' amounts
                // Example: If SB posts 1 and calls a raise to 6, the 'Call' amount might be 5.
                // The current logic assumes 'amount' is the total contribution for that action.
                // If 'amount' represents the *additional* amount, blind posting needs separate handling.
                // For simplicity, assuming current logic is correct based on data structure.
            }
        });

        // Initial blind deduction (simplified - applied *before* any actions)
        // This might double-deduct if blinds are included in later 'Call' amounts.
        // A more robust approach would integrate blind posting as the first actions.
        if (currentStreetIndex === 0 && currentActionIndex < 0) { // Before first preflop action
            if (p.position === 'SB') currentChips -= handData.metadata.stakes.smallBlind;
            if (p.position === 'BB') currentChips -= handData.metadata.stakes.bigBlind;
        }


        // Determine cards to show
        let cardsToShow: CardRepresentation[] = []; // Use CardRepresentation
        const isShowdown = currentStreetIndex === 4;

        if (hasFolded) {
            cardsToShow = []; // Folded players show no cards
        } else if (p.isHero && handData.heroCards) {
            // Explicitly cast hero cards to CardCode
            cardsToShow = [handData.heroCards.holeCard1 as CardCode, handData.heroCards.holeCard2 as CardCode];
        } else if (isShowdown) {
            // TODO: Logic to get actual villain cards at showdown from data source
            // Need to access villain hole cards if available in handData at showdown
            // For now, assume they are not available or show backs
            cardsToShow = ['back', 'back']; // Show backs if no data or villain
        } else {
            // Non-hero, pre-showdown
            cardsToShow = ['back', 'back']; // Show card backs
        }

        return {
            id: p.id,
            name: p.name,
            cards: cardsToShow, // Use CardRepresentation
            chips: Math.max(0, currentChips), // Ensure chips don't go negative visually
            isHero: p.isHero, // Pass through isHero
            isFolded: hasFolded,
            winner: false, // TODO: Add winner logic at showdown
            revealAlways: p.isHero, // Optionally always show hero - controlled by PlayerRow revealHole prop now
            position: p.position, // Ensure position is passed through
        };
    });
};


// Prepare board cards based on current state
 const getBoardCardsForRender = (
     handData: NewHandSummaryJson,
     currentStreetIndex: number // Use 0-4 index
 ): CardCode[] => {
     if (!handData) return [];
     let board: CardCode[] = [];
     // Use optional chaining and nullish coalescing for safety
     if (currentStreetIndex >= 1) board = board.concat(handData.flop?.cardsDealt?.map(c => c as CardCode) ?? []);
     if (currentStreetIndex >= 2) board = board.concat(handData.turn?.cardsDealt?.map(c => c as CardCode) ?? []);
     if (currentStreetIndex >= 3) board = board.concat(handData.river?.cardsDealt?.map(c => c as CardCode) ?? []); 
     return board; 
 };

 // Calculate current pot size based on current step
 const getCurrentPot = (
    handData: NewHandSummaryJson,
    currentStreetIndex: number, // Use index 0-3 for actions, pot exists from preflop
    currentActionIndex: number
 ): number => {
     if(!handData || !handData.metadata?.stakes) return 0; // Add null check for stakes
     // Start with blinds/antes posted before any actions
     let pot = 0;
     // Check if preflop or later to add initial blinds
     if (currentStreetIndex >= 0) {
         pot = handData.metadata.stakes.smallBlind + handData.metadata.stakes.bigBlind;
         // Add ante logic here if necessary (e.g., sum ante * num players)
     }

     // Ensure actions are only calculated up to the current point
     const relevantStreetIndex = Math.min(currentStreetIndex, 3); // Actions only up to river
     const actions = getActionsUpToCurrent(handData, relevantStreetIndex, currentActionIndex);

     actions.forEach(action => {
        // Only add amounts from Bet, Raise, Call actions to the pot
        if(action.type === 'Bet' || action.type === 'Raise' || action.type === 'Call') {
           // Ensure amount is treated as a number, default to 0 if undefined/null
           pot += Number(action.amount || 0);
        }
        // Note: This assumes 'amount' in Call/Raise includes the blind/previous bet.
        // If 'amount' is only the *additional* amount, this calculation might be off.
     });
     return pot;
 }


// ---------- Custom Hook for Layout Calculation ----------
interface SeatMapEntry {
  index: number;
  seatX: number; seatY: number;
  cardX: number; cardY: number;
  chipFraction: number; // Offset fraction for chip placement
  rotationOffsetRad: number;
  cardRotationDeg: number; // Rotation for the card group
}

function useTableLayout(players: ComponentPlayer[]) {
    return React.useMemo(() => {
        // --- Start of memoized calculation --- 
        // 1. Get unique active positions, sorted
        const activePositions = players.map(p => p.position).filter((v, i, a) => a.indexOf(v) === i)
                                     .sort((a, b) => {
                                         const indexA = tableConfig.POSITION_ORDER_9_HANDED.indexOf(a);
                                         const indexB = tableConfig.POSITION_ORDER_9_HANDED.indexOf(b);
                                         if (indexA === -1 && indexB === -1) return 0; 
                                         if (indexA === -1) return 1; 
                                         if (indexB === -1) return -1;
                                         return indexA - indexB;
                                     });
        const numActivePlayers = activePositions.length;

        // 2. Determine hero index and calculate rotation offset FIRST
        let rotationOffsetRad = 0; 
        const heroPosition = players.find(p => p.isHero)?.position;
        const heroIndex = heroPosition ? tableConfig.POSITION_ORDER_9_HANDED.indexOf(heroPosition) : -1;

        if (heroIndex !== -1 && numActivePlayers > 0) {
            const heroFraction = heroIndex / numActivePlayers;
            const heroBaseAngleDegrees = 360 * heroFraction - 90; 
            const heroOffsetDeg = tableConfig.HERO_TARGET_ANGLE - heroBaseAngleDegrees;
            rotationOffsetRad = heroOffsetDeg * Math.PI / 180;
        } 

        // 3. Build the seatMap using the calculated rotation offset
        const seatMap = new Map<string, SeatMapEntry>();
        
        // Define radii calculations using ELEMENT_PADDING for the single ring
        const seatRx = tableConfig.TABLE_RX + tableConfig.BORDER_WIDTH/2 + tableConfig.SEAT_RADIUS + 2;
        const seatRy = tableConfig.TABLE_RY + tableConfig.BORDER_WIDTH/2 + tableConfig.SEAT_RADIUS + 2;
        
        // Calculate radii for the single element ring (cards and chips)
        // Remove subtraction of half the max element dimension AGAIN to align outer edge
        const elementRx = tableConfig.TABLE_RX - tableConfig.BORDER_WIDTH/2 - tableConfig.ELEMENT_PADDING;
        const elementRy = tableConfig.TABLE_RY - tableConfig.BORDER_WIDTH/2 - tableConfig.ELEMENT_PADDING;

        // Calculate radii for the bet line ellipse
        const betLineRx = elementRx - tableConfig.BET_LINE_PADDING;
        const betLineRy = elementRy - tableConfig.BET_LINE_PADDING;

        // Calculate chip angular offset in radians
        const chipOffsetRad = tableConfig.CHIP_ANGULAR_OFFSET_DEG * Math.PI / 180;
        const chipFracOffset = chipOffsetRad / (2 * Math.PI);

        activePositions.forEach((pos, i) => {
            const baseFrac = numActivePlayers > 0 ? i / numActivePlayers : 0;

            // Calculate angle for positioning (used by polarEllipse)
            const baseAngleRad = 2 * Math.PI * baseFrac - Math.PI/2;
            const finalAngleRad = baseAngleRad + rotationOffsetRad;

            // 1. Calculate position on the outer Seat ring (unchanged)
            const seat = polarEllipse(tableConfig.TABLE_CX, tableConfig.TABLE_CY, seatRx, seatRy, baseFrac, rotationOffsetRad);

            // --- Start Complex Alignment --- 
            
            // 2. Calculate position on the "outer edge" ellipse
            const outerEdgePos = polarEllipse(tableConfig.TABLE_CX, tableConfig.TABLE_CY, elementRx, elementRy, baseFrac, rotationOffsetRad);

            // 3. Calculate coordinates relative to ellipse center for angle calculations
            const x_rel = outerEdgePos.x - tableConfig.TABLE_CX;
            const y_rel = outerEdgePos.y - tableConfig.TABLE_CY;
            
            // Avoid division by zero at poles, though atan2 should handle it
            const epsilon = 1e-6;
            if (Math.abs(x_rel) < epsilon && Math.abs(y_rel) < epsilon) {
              // Should not happen if rx, ry > 0
              console.warn("Near zero relative coords in ellipse calculation");
            }

            // 4. Calculate the angle of the inward normal vector
            // Inward normal vector is proportional to (-rx^2 * y_rel, -ry^2 * x_rel) ? No, (-ry^2 * x_rel, -rx^2 * y_rel)
            // Let's re-derive: tangent slope = -(b^2*x)/(a^2*y). Normal slope = (a^2*y)/(b^2*x)
            // Inward normal angle: atan2(-a^2*y, -b^2*x). a=elementRx, b=elementRy
            const inwardNormalAngleRad = Math.atan2(
                -elementRx * elementRx * y_rel, 
                -elementRy * elementRy * x_rel 
            );
            
            // 5. Calculate the offset distance (half card height)
            const cardOffsetDistance = tableConfig.CARD_SVG_HEIGHT / 2;
            
            // 6. Calculate offset vector components along the inward normal
            const offsetX = cardOffsetDistance * Math.cos(inwardNormalAngleRad);
            const offsetY = cardOffsetDistance * Math.sin(inwardNormalAngleRad);

            // 7. Calculate the final Card Center Position by applying the offset
            const finalCardX = outerEdgePos.x + offsetX; // Add offset vector
            const finalCardY = outerEdgePos.y + offsetY;

            // 8. Calculate the tangent angle for card rotation
            // Tangent vector is proportional to (a^2*y, -b^2*x)
            const tangentAngleRad = Math.atan2(
                -elementRy * elementRy * x_rel, 
                 elementRx * elementRx * y_rel
            );
            const cardRotationDeg = tangentAngleRad * 180 / Math.PI;

            // --- End Complex Alignment --- 
            
            // Calculate CHIP fraction (still relative to the original baseFrac)
            let chipFraction = (baseFrac + chipFracOffset) % 1;
            if (chipFraction < 0) chipFraction += 1;

            seatMap.set(pos, {
              index: i,
              seatX: seat.x,  seatY: seat.y,
              cardX: finalCardX,  cardY: finalCardY, // Use the precisely offset position
              chipFraction: chipFraction,
              rotationOffsetRad: rotationOffsetRad,
              cardRotationDeg: cardRotationDeg, // Use the tangent angle for rotation
            });
        });

        // Return the single ellipse radii and the rotation offset
        return { seatMap, elementRx, elementRy, betLineRx, betLineRy, rotationOffsetRad };
    }, [players]);
}

// Type for the animated chip data (Restore)
interface AnimatedChipData {
    key: string; 
    playerId: number; 
    amount: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    denomination: number; 
}

// ---------- Stepper Component (Final SVG Wiring) ----------
export const PokerHandStepper: FC<StepperProps> = ({ 
    title = 'Hand', pot = 0, board, players, 
    currentStreetIndex, currentActionIndex, 
    onStepChange, handDataSource, animatedChips, seatMap
}) => {
    const street = currentStreetIndex;
    const next = useCallback(() => onStepChange('next'), [onStepChange]);
    const prev = useCallback(() => onStepChange('prev'), [onStepChange]);

    useEffect(() => {
        const handler = (e: KeyboardEvent<Window>) => {
            if (e.key === 'ArrowRight') next();
            if (e.key === 'ArrowLeft') prev();
        };
        window.addEventListener('keydown', handler as unknown as EventListener);
        return () => window.removeEventListener('keydown', handler as unknown as EventListener);
    }, [next, prev]);

    // Stepper calculates its own layout now
    const { elementRx, elementRy, betLineRx, betLineRy, rotationOffsetRad } = useTableLayout(players);

    const numActivePlayers = players.length;
    type StepperPlayerElement = ComponentPlayer & { /* ... as before including baseFrac ... */ 
        seatX: number; seatY: number; 
        cardX: number; cardY: number; 
        chipFraction: number; rotationOffsetRad: number; 
        cardRotationDeg: number; baseFrac: number; 
        revealHole: boolean; isFolded: boolean 
    };
    
    // Player elements memoization (unchanged from previous working state)
    const playerElements = React.useMemo((): StepperPlayerElement[] => { 
        return players.map((p: ComponentPlayer) => {
            const d = seatMap.get(p.position); 
            if (!d) throw new Error(`Layout data not found for position: ${p.position}`);
            const baseFrac = numActivePlayers > 0 ? d.index / numActivePlayers : 0; 
            return {
                ...p, 
                seatX: d.seatX, seatY: d.seatY,
                cardX: d.cardX, cardY: d.cardY,
                chipFraction: d.chipFraction,
                rotationOffsetRad: d.rotationOffsetRad,
                cardRotationDeg: d.cardRotationDeg,
                baseFrac: baseFrac, 
                revealHole: currentStreetIndex === 4 || (p.revealAlways ?? false),
                isFolded: p.isFolded ?? false,
            };
        });
    }, [players, seatMap, currentStreetIndex, numActivePlayers]);

    const boardCenterX = tableConfig.TABLE_CX;
    const boardCenterY = tableConfig.TABLE_CY; 
    const dealerAreaX = tableConfig.TABLE_CX;
    const dealerAreaY = tableConfig.TABLE_CY - (tableConfig.CARD_SVG_HEIGHT / 2) - 45;

    // --- Render SVG --- 
    return (
        <div className="ph-root-svg"> 
            <svg 
                viewBox={`0 0 ${tableConfig.SVG_VIEWBOX_WIDTH} ${tableConfig.SVG_VIEWBOX_HEIGHT}`} 
                width="100%" 
                preserveAspectRatio="xMidYMid meet"
                fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
                fontWeight="bold"
            >
                <defs>
                    {/* === Reusable Definitions === */}
                    {/* Seat Gradient */}
                    <linearGradient id="seatGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{stopColor:'#2b6cb0', stopOpacity:1}} />
                        <stop offset="100%" style={{stopColor:'#1a3f7a', stopOpacity:1}} />
                    </linearGradient>
                    {/* Chip Shadow Filter */}
                    <filter id="chip-shadow" x="-20%" y="-20%" width="140%" height="140%">
                       <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000000" floodOpacity="0.5"/>
                    </filter>
                    {/* Chip Stripe Definitions */}
                    <rect id="edgeStripeSvg" x="51" y="4" width="8" height="16" rx="1" ry="1" fill="#ffffff"/>
                    <clipPath id="rimClipSvg"><circle cx="55" cy="55" r="50"/></clipPath>
                    {/* Card Back Lattice Pattern */}
                    <pattern id="lattice" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="28" height="3" y="0" fill="#f4f4f4" shapeRendering="crispEdges"/> 
                        <rect width="3" height="28" x="0" fill="#f4f4f4" shapeRendering="crispEdges"/> 
                    </pattern>
                </defs>

                {/* 1. Table Background & Border - Use config */}
                <ellipse cx={tableConfig.TABLE_CX} cy={tableConfig.TABLE_CY} rx={tableConfig.TABLE_RX + tableConfig.BORDER_WIDTH / 2} ry={tableConfig.TABLE_RY + tableConfig.BORDER_WIDTH / 2} fill="#4a2d18" />
                <ellipse cx={tableConfig.TABLE_CX} cy={tableConfig.TABLE_CY} rx={tableConfig.TABLE_RX - tableConfig.BORDER_WIDTH / 2} ry={tableConfig.TABLE_RY - tableConfig.BORDER_WIDTH / 2} fill="#157038" />
                
                {/* Bet Line Ellipse */}
                <ellipse
                  cx={tableConfig.TABLE_CX} cy={tableConfig.TABLE_CY}
                  rx={betLineRx}
                  ry={betLineRy}
                  fill="none" 
                  stroke="rgba(255, 255, 255, 0.3)" // Light, semi-transparent white
                  strokeWidth="2"
                  strokeDasharray="8 12" // Dashed pattern
                />

                {/* Debug ring for Elements (Orange) */}
                <ellipse
                  cx={tableConfig.TABLE_CX} cy={tableConfig.TABLE_CY}
                  rx={elementRx} 
                  ry={elementRy}
                  fill="none" stroke="orange" strokeDasharray="4 4" strokeWidth={1}
                />

                {/* Player Elements */}
                {playerElements.map((p) => (
                    <g key={p.name} className={`player-group ${p.isFolded ? 'folded' : ''}`}>
                         {/* Seat */}
                         <motion.g
                             initial={{ opacity: 0, scale: 0.5 }}
                             animate={{ opacity: 1, scale: 1 }}
                             transition={{ type: 'spring', damping: 15, stiffness: 100, delay: 0.10 }}
                         >
                             <PlayerSeat 
                                 position={p.position}
                                 isHero={p.isHero}
                                 isFolded={p.isFolded}
                                 x={p.seatX} 
                                 y={p.seatY} 
                             />
                         </motion.g>
                         {/* Chips */}
                         <motion.g
                             initial={{ opacity: 0, scale: 0.5 }}
                             animate={{ opacity: p.isFolded ? 0.6 : 1, scale: 1 }}
                             transition={{ type: 'spring', damping: 15, stiffness: 100, delay: 0.05 }}
                         >
                             <ChipStacksSvg 
                                chips={p.chips}
                                isFolded={p.isFolded}
                                fraction={p.chipFraction} // Ensure passing chipFraction
                                rotationOffsetRad={p.rotationOffsetRad}
                                chipRx={elementRx} // Ensure passing elementRx/Ry
                                chipRy={elementRy}
                             />
                         </motion.g>
                         {/* Cards */}
                         <motion.g
                             initial={{ opacity: 0, scale: 0.5 }}
                             animate={{ opacity: p.isFolded ? 0 : 1, scale: 1 }}
                             transition={{ type: 'spring', damping: 15, stiffness: 100, delay: 0.15 }}
                         >
                             <PlayerCards 
                                cards={p.cards}
                                show={p.revealHole}
                                isFolded={p.isFolded}
                                x={p.cardX} 
                                y={p.cardY} 
                                cardRotationDeg={p.cardRotationDeg} // Pass the rotation prop
                             />
                          </motion.g>
                    </g>
                ))}

                {/* Re-insert Board Component */}
                <Board
                  cards={board} // Prop passed from parent
                  street={street} // Prop passed from parent (currentStreetIndex)
                  x={boardCenterX}
                  y={boardCenterY}
                />

                {/* Pot Area */}
                <g transform={`translate(${dealerAreaX}, ${dealerAreaY})`} textAnchor="middle">
                   <rect x="-70" y="-25" width="140" height="50" rx="15" fill="rgba(10,10,10,0.6)" stroke="rgba(80,80,80,0.8)" />
                   <text y="-5" fill="#fff" fontSize="18" opacity="0.8">{STREETS[street]}</text>
                   <text y="20" fill="#fff" fontSize="22" fontWeight="bold">
                      <tspan dx="-1.2em">💰</tspan> 
                      {pot}
                    </text>
                </g>

                {/* Render Placed Bet Chips using animatedChips array */}
                <AnimatePresence>
                     {animatedChips.map((chip) => (
                         <motion.g
                            key={chip.key} // Unique key from parent state
                            initial={{
                                // Start at player stack position
                                x: chip.startX - CHIP_SVG_CENTER * CHIP_SCALE,
                                y: chip.startY - CHIP_SVG_CENTER * CHIP_SCALE,
                                scale: CHIP_SCALE,
                                opacity: 0.8, // Start slightly transparent?
                            }}
                            animate={{
                                // Animate to bet line position
                                x: chip.endX - CHIP_SVG_CENTER * CHIP_SCALE,
                                y: chip.endY - CHIP_SVG_CENTER * CHIP_SCALE,
                                scale: CHIP_SCALE, // Keep scale
                                opacity: 1,     // Fully opaque
                                transition: { duration: 0.4, ease: "easeOut" }
                            }}
                            // NO exit prop - chips stay until cleared from array by parent
                        >
                            <SvgChip denomination={chip.denomination} />
                        </motion.g>
                    ))}
                </AnimatePresence>

            </svg>
            
            {/* Navigation remains outside SVG */}
            <div className="ph-nav">
                <button className="ph-btn" onClick={prev} disabled={street === 0}>‹</button>
                <button className="ph-btn" onClick={next} disabled={street === STREETS.length - 1}>›</button>
            </div>
        </div>
    );
};


// ---------- Main Component Wrapper ----------
// Rename PokerHandReplayer to PokerHand and make it the default export
const PokerHand: FC = () => {
    const { handId } = useParams<{ handId: string }>();
    const [handDataSource, setHandDataSource] = useState<NewHandSummaryJson | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentStreetIndex, setCurrentStreetIndex] = useState(0);
    const [currentActionIndex, setCurrentActionIndex] = useState(-1);
    // State for chips currently being animated/placed on the table this street
    const [animatedChips, setAnimatedChips] = useState<AnimatedChipData[]>([]);

    // Calculate layout map (needed for chip start/end positions)
    const playersForLayout = handDataSource ? getPlayerDataForRender(handDataSource, currentStreetIndex, currentActionIndex) : [];
    const { seatMap, elementRx, elementRy, betLineRx, betLineRy, rotationOffsetRad } = useTableLayout(playersForLayout);

    // Calculate action counts per street (memoized)
    const actionCounts = React.useMemo(() => {
        if (!handDataSource) return { preflop: 0, flop: 0, turn: 0, river: 0 };
        const getCount = (street: AppStreet) => handDataSource[street]?.actions?.length ?? 0;
        return { preflop: getCount('preflop'), flop: getCount('flop'), turn: getCount('turn'), river: getCount('river') };
    }, [handDataSource]);

    // --- Stepping Logic (Clear chips on street change) --- 
    const handleStepChange = useCallback((direction: 'next' | 'prev') => {
        const currentStreet = INDEX_TO_APP_STREET[currentStreetIndex];
        const numActionsOnCurrentStreet = actionCounts[currentStreet] ?? 0;
        const maxStreetIndex = STREETS.length - 1;
        let nextStreet = currentStreetIndex;
        let nextAction = currentActionIndex;
        let clearBets = false; // Renamed from clearChips for clarity

        if (direction === 'next') {
            if (currentStreetIndex === maxStreetIndex) return;
            const potentialNextActionIndex = currentActionIndex + 1;
            if (potentialNextActionIndex < numActionsOnCurrentStreet) {
                nextAction = potentialNextActionIndex;
            } else {
                const potentialNextStreetIndex = currentStreetIndex + 1;
                if (potentialNextStreetIndex <= maxStreetIndex) {
                    nextStreet = potentialNextStreetIndex;
                    nextAction = -1;
                    clearBets = true; // Clear chips on street change
                } 
            }
        } else { // direction === 'prev'
            if (currentStreetIndex === 0 && currentActionIndex < 0) return;
            const potentialPrevActionIndex = currentActionIndex - 1;
            if (potentialPrevActionIndex >= -1) {
                nextAction = potentialPrevActionIndex;
                 // Remove the chip associated with the action we are moving *away* from
                const actionToUndo = handDataSource?.[currentStreet]?.actions?.[currentActionIndex];
                if(actionToUndo) {
                   setAnimatedChips(prevChips => prevChips.filter(c => c.key !== `${currentStreetIndex}-${currentActionIndex}-${actionToUndo.playerId}-${actionToUndo.type}`));
                }
            } else {
                const potentialPrevStreetIndex = currentStreetIndex - 1;
                if (potentialPrevStreetIndex >= 0) {
                    nextStreet = potentialPrevStreetIndex;
                    const prevStreet = INDEX_TO_APP_STREET[nextStreet];
                    nextAction = (actionCounts[prevStreet] ?? 0) - 1;
                    clearBets = true; // Also clear chips when going back a street
                }
            }
        }
        // Update state
        if(nextStreet !== currentStreetIndex) {
             setCurrentStreetIndex(nextStreet);
             if(clearBets) setAnimatedChips([]); // Clear chips array
        }
        // Set action index AFTER potentially clearing chips for the new street
        if(nextAction !== currentActionIndex) setCurrentActionIndex(nextAction);

    }, [currentStreetIndex, currentActionIndex, actionCounts, handDataSource]);

    // --- Effect to Add New Animating Chips --- 
    useEffect(() => {
        if (!handDataSource || currentActionIndex < 0 || currentStreetIndex >= INDEX_TO_APP_STREET.length) {
            return; 
        }
        const streetKey = INDEX_TO_APP_STREET[currentStreetIndex];
        const action = handDataSource[streetKey]?.actions?.[currentActionIndex];

        if (action && (action.type === 'Bet' || action.type === 'Call' || action.type === 'Raise')) {
            const playerState = playersForLayout.find(p => p.id === action.playerId);
            if (!playerState) return; 
            const playerLayout = seatMap.get(playerState.position); 
            
            if (playerLayout && !playerState.isFolded) {
                const numActivePlayers = playersForLayout.length;
                const baseFrac = numActivePlayers > 0 ? playerLayout.index / numActivePlayers : 0;

                const startPos = { x: playerLayout.seatX, y: playerLayout.seatY };
                
                // Calculate specific target radii for the chip destination
                const chipTargetRx = betLineRx - tableConfig.BET_CHIP_TARGET_OFFSET;
                const chipTargetRy = betLineRy - tableConfig.BET_CHIP_TARGET_OFFSET;
                
                // Calculate end position using the further inset chipTargetRx/Ry
                const endPos = polarEllipse(tableConfig.TABLE_CX, tableConfig.TABLE_CY, chipTargetRx, chipTargetRy, baseFrac, playerLayout.rotationOffsetRad);
                
                const amount = action.amount ?? 0;
                let denomToAnimate = 1;
                if (amount >= 500) denomToAnimate = 500;
                else if (amount >= 100) denomToAnimate = 100;
                else if (amount >= 25) denomToAnimate = 25;
                else if (amount >= 5) denomToAnimate = 5;
                
                const animationKey = `${currentStreetIndex}-${currentActionIndex}-${action.playerId}-${action.type}`;
                
                const newChipData: AnimatedChipData = {
                    key: animationKey,
                    playerId: action.playerId,
                    amount: amount,
                    startX: startPos.x, 
                    startY: startPos.y, 
                    endX: endPos.x, 
                    endY: endPos.y, 
                    denomination: denomToAnimate,
                };

                setAnimatedChips(prevChips => {
                    if (prevChips.some(c => c.key === newChipData.key)) return prevChips;
                    return [...prevChips, newChipData];
                });
            }
        }
    }, [handDataSource, currentStreetIndex, currentActionIndex, seatMap, elementRx, elementRy, betLineRx, betLineRy, playersForLayout]);

    // --- Data Fetching --- 
    useEffect(() => { 
        console.log('Visualizing hand ID:', handId ?? 'mockHand123');
        const loadHandData = async () => {
            setLoading(true);
            setError(null);
            setCurrentStreetIndex(0); 
            setCurrentActionIndex(-1);
            setAnimatedChips([]); // Clear chips on new hand load
            try {
                await new Promise(resolve => setTimeout(resolve, 300)); 
                console.log('Using mock hand data.');
                setHandDataSource(appMockHandData);
            } catch (err) {
                console.error("Error loading hand data:", err);
                setError(err instanceof Error ? err.message : 'Failed to load hand data');
                setHandDataSource(null);
            } finally {
                setLoading(false);
            }
        };
        loadHandData();
    }, [handId]);

    // --- Derived State for Stepper --- 
    const boardForStepper = handDataSource ? getBoardCardsForRender(handDataSource, currentStreetIndex) : [];
    const potForStepper = handDataSource ? getCurrentPot(handDataSource, currentStreetIndex, currentActionIndex) : 0;

    if (loading) return <div className="visualiser-loading">Loading Hand Replay...</div>;
    if (error) return <div className="visualiser-error">Error: {error}</div>;
    if (!handDataSource) return <div className="visualiser-error">Hand data not found or invalid.</div>;

    const handTitle = `Hand: ${handDataSource.metadata.handId} (${handDataSource.metadata.stakes.smallBlind}/${handDataSource.metadata.stakes.bigBlind})`;

    return (
        <div className="visualiser-page-container">
             <PokerHandStepper
                title={handTitle}
                pot={potForStepper}
                board={boardForStepper}
                players={playersForLayout} 
                currentStreetIndex={currentStreetIndex} 
                currentActionIndex={currentActionIndex} 
                onStepChange={handleStepChange} 
                handDataSource={handDataSource} 
                animatedChips={animatedChips} // Pass the array of chips
                seatMap={seatMap}
             />
        </div>
    );
};


// Make PokerHand the default export
export default PokerHand;